import { Module } from "vuex";
import { State } from "../store";
import {
  User,
  Unsubscribe,
  GoogleAuthProvider,
  TwitterAuthProvider,
  signInWithRedirect,
  linkWithRedirect,
  signOut,
  onAuthStateChanged,
  deleteUser,
  getIdTokenResult,
  ParsedToken,
} from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "@/plugins/firebase";
import { UserData } from "@/common/types/UserData";
import { deserializeDate, SerializedDate } from "@/common/types/SerializedDate";

export type AuthState = {
  user: User | null;
  userData: UserData | null;
  claims: ParsedToken | null;
};

let unsubscribe: Unsubscribe | null = null;

const authModule: Module<AuthState, State> = {
  namespaced: true,
  state: {
    user: null as User | null,
    userData: null as UserData | null,
    claims: null as ParsedToken | null,
  },
  mutations: {
    setUser(state, user) {
      state.user = user;
    },
    setUserData(state, userData) {
      state.userData = userData;
    },
    setClaims(state, claims) {
      state.claims = claims;
    },
  },
  actions: {
    loginWithGoogle() {
      const provider = new GoogleAuthProvider();
      return signInWithRedirect(auth, provider);
    },
    loginWithTwitter() {
      const provider = new TwitterAuthProvider();
      return signInWithRedirect(auth, provider);
    },
    linkWithGoogle({ state }) {
      const provider = new GoogleAuthProvider();
      return state.user ? linkWithRedirect(state.user, provider) : Promise.reject(new Error());
    },
    linkWithTwitter({ state }) {
      const provider = new TwitterAuthProvider();
      return state.user ? linkWithRedirect(state.user, provider) : Promise.reject(new Error());
    },
    logout() {
      return signOut(auth);
    },
    fetchUser() {
      return new Promise((resolve) => {
        const unsub = onAuthStateChanged(auth, (user) => {
          unsub();
          return resolve(user);
        });
      });
    },
    async updateUser({ commit, dispatch }, data: UserData) {
      const user = auth.currentUser;
      if (user === null) {
        return;
      }
      const fn = httpsCallable<UserData, UserData>(functions, "user-update");
      const updateData = (await fn(data)).data;
      commit("setUserData", updateData);

      const userData = await dispatch("user/getUser", user.uid, { root: true });
      commit("user/setUser", { ...userData, ...updateData }, { root: true });
    },
    startListener({ commit, dispatch }) {
      unsubscribe = onAuthStateChanged(auth, async (user) => {
        if (user !== null) {
          dispatch(
            "loading/wrapOverlayLoading",
            () =>
              getIdTokenResult(user).then((token) => {
                commit("setClaims", token.claims);
              }),
            { root: true }
          );

          const fn = httpsCallable<{ uid: string }, SerializedDate<UserData>>(functions, "user-get");

          dispatch(
            "loading/wrapOverlayLoading",
            async () => commit("setUserData", deserializeDate((await fn({ uid: user.uid })).data)),
            { root: true }
          );

          commit("setUserData", deserializeDate((await fn({ uid: user.uid })).data));
        } else {
          commit("setUserData", null);
          commit("setClaims", null);
        }
        commit("setUser", user);
      });
    },
    endListener() {
      if (unsubscribe) {
        unsubscribe();
      }
    },
    deleteUser(context) {
      if (context.state.user === null) {
        return null;
      }
      return deleteUser(context.state.user);
    },
  },
};

export default authModule;
